import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { ROLES } from '@/lib/roles';
import { groupCapabilitiesByModule, roleCapabilityModuleLabels } from '@/lib/permissionRegistry';
import DashboardLayout from '@/components/DashboardLayout';
import WorshipPermissionControls from '@/components/WorshipPermissionControls';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Plus, 
  Users,
  MoreVertical,
  Pencil,
  Trash2,
  Shield,
  Mail,
  Copy,
  Check
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TeamPage = () => {
  const { user, hasCapability, isAdmin } = useAuth();
  const canGrantWorshipAccess = hasCapability('worship.grant_access');
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    roles: ['employee']
  });
  const [capabilities, setCapabilities] = useState([]);
  const [permissionOverrides, setPermissionOverrides] = useState({});
  const [worshipPermissionSaving, setWorshipPermissionSaving] = useState(null);
  const capabilityGroups = groupCapabilitiesByModule(capabilities);
  const visibleCapabilityCount = capabilityGroups.reduce(
    (count, group) => count + group.capabilities.length,
    0,
  );

  useEffect(() => {
    fetchMembers();
    fetchCapabilities();
  }, []);

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API}/team`);
      setMembers(response.data);
    } catch (error) {
      toast.error('Kon teamleden niet laden');
    } finally {
      setLoading(false);
    }
  };

  const fetchCapabilities = async () => {
    try {
      const response = await axios.get(`${API}/team/capabilities`);
      setCapabilities(response.data);
    } catch (error) {
      // Rolrechten-sectie blijft dan gewoon leeg; geen blokkerende fout.
    }
  };

  const setOverride = (key, value) => {
    setPermissionOverrides((prev) => {
      const next = { ...prev };
      if (value === null) {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
  };

  const handleInvite = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email) {
      toast.error('Vul alle verplichte velden in');
      return;
    }
    if (!formData.roles.length) {
      toast.error('Kies minstens één rol');
      return;
    }

    try {
      const response = await axios.post(`${API}/team/invite`, null, {
        params: {
          name: formData.name,
          email: formData.email,
          roles: formData.roles
        }
      });
      
      setInviteResult(response.data);
      toast.success('Teamlid uitgenodigd');
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Uitnodigen mislukt');
    }
  };

  const handleUpdate = async (memberId, data) => {
    try {
      await axios.put(`${API}/team/${memberId}`, data);
      toast.success('Teamlid bijgewerkt');
      fetchMembers();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Bijwerken mislukt');
    }
  };

  const updateWorshipPermission = async (permission, effect) => {
    setWorshipPermissionSaving(permission);
    try {
      const response = await axios.put(`${API}/worship/team/permissions/${editingMember.id}`, {
        permission,
        effect,
      });
      setMembers((items) => items.map((item) => item.id === editingMember.id ? { ...item, ...response.data } : item));
      setEditingMember((item) => ({ ...item, ...response.data }));
      toast.success(effect === 'inherit' ? 'Worshiprecht erft weer van de rol' : 'Worshiprecht bijgewerkt');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Worshiprecht wijzigen mislukt');
    } finally {
      setWorshipPermissionSaving(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Weet je zeker dat je dit teamlid wilt verwijderen?')) return;
    
    try {
      await axios.delete(`${API}/team/${id}`);
      toast.success('Teamlid verwijderd');
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verwijderen mislukt');
    }
  };

  const openEditDialog = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name,
      email: member.email,
      roles: member.roles && member.roles.length ? member.roles : ['employee']
    });
    setPermissionOverrides(member.permission_overrides || {});
    setInviteResult(null);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingMember(null);
    setInviteResult(null);
    setCopiedPassword(false);
    setPermissionOverrides({});
    setFormData({
      name: '',
      email: '',
      roles: ['employee']
    });
  };

  const toggleFormRole = (roleValue) => {
    setFormData((current) => ({
      ...current,
      roles: current.roles.includes(roleValue)
        ? current.roles.filter((r) => r !== roleValue)
        : [...current.roles, roleValue],
    }));
  };

  const copyPassword = () => {
    if (inviteResult?.invite_link) {
      navigator.clipboard.writeText(inviteResult.invite_link);
      setCopiedPassword(true);
      setTimeout(() => setCopiedPassword(false), 2000);
    }
  };

  const getRoleBadges = (roles) => {
    const list = roles && roles.length ? roles : ['employee'];
    return list.map((role) => ROLES.find(r => r.value === role) || ROLES.find(r => r.value === 'employee'));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-40 bg-slate-200 rounded-2xl"></div>
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="team-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Team</h1>
            <p className="text-slate-500">Beheer teamleden en rollen</p>
          </div>
          {isAdmin && (
            <Button 
              onClick={() => {
                resetForm();
                setDialogOpen(true);
              }}
              className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
              data-testid="invite-member-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Teamlid uitnodigen
            </Button>
          )}
        </div>

        {/* Team Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {members.map((member) => {
            const roleBadges = getRoleBadges(member.roles);
            const isCurrentUser = member.id === user?.id;
            
            return (
              <Card key={member.id} className="border-0 shadow-sm hover:shadow-md transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center text-teal-700 font-semibold text-lg">
                        {member.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-900">{member.name}</h3>
                          {isCurrentUser && (
                            <span className="text-xs text-teal-600">(jij)</span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {roleBadges.map((rb) => (
                            <Badge key={rb.value} className={rb.color}>{rb.label}</Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    {isAdmin && !isCurrentUser && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(member)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Bewerken
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDelete(member.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Verwijderen
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                  
                  <div className="space-y-2 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <span className="truncate">{member.email}</span>
                    </div>
                    {member.phone && (
                      <div className="flex items-center gap-2">
                        <span className="w-4 h-4 text-slate-400">📱</span>
                        <span>{member.phone}</span>
                      </div>
                    )}
                  </div>
                  
                  {!member.is_active && (
                    <span className="inline-block mt-3 text-xs bg-red-100 text-red-600 px-2 py-1 rounded">
                      Inactief
                    </span>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {members.length === 0 && (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">Geen teamleden</h3>
              <p className="text-slate-500 mb-4">
                Je bent de enige gebruiker. Nodig teamleden uit om samen te werken.
              </p>
              {isAdmin && (
                <Button 
                  onClick={() => setDialogOpen(true)}
                  className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Eerste teamlid uitnodigen
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Invite/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingMember ? 'Teamlid bewerken' : 'Teamlid uitnodigen'}
              </DialogTitle>
            </DialogHeader>
            
            {inviteResult ? (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 rounded-xl text-center">
                  <Check className="w-12 h-12 text-green-600 mx-auto mb-2" />
                  <h3 className="font-medium text-green-800">Uitnodiging verstuurd!</h3>
                  <p className="text-sm text-green-600 mt-1">
                    {inviteResult.user.name} heeft een welkomstmail ontvangen om zelf een wachtwoord in te stellen.
                  </p>
                </div>

                <div className="p-4 bg-amber-50 rounded-xl">
                  <p className="text-sm font-medium text-amber-800 mb-2">
                    Uitnodigingslink (reserve, als de mail niet aankomt):
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 p-2 bg-white rounded text-amber-900 font-mono text-xs break-all">
                      {inviteResult.invite_link}
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={copyPassword}
                      className="shrink-0"
                    >
                      {copiedPassword ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-amber-600 mt-2">
                    Deze link is 7 dagen geldig en kan maar één keer gebruikt worden.
                  </p>
                </div>
                
                <Button 
                  onClick={() => {
                    setDialogOpen(false);
                    resetForm();
                  }}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
                >
                  Sluiten
                </Button>
              </div>
            ) : (
              <form onSubmit={editingMember ? (e) => {
                e.preventDefault();
                if (!formData.roles.length) {
                  toast.error('Kies minstens één rol');
                  return;
                }
                handleUpdate(editingMember.id, { name: formData.name, roles: formData.roles, permission_overrides: permissionOverrides });
              } : handleInvite} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Naam *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Volledige naam"
                    className="rounded-xl"
                    data-testid="member-name-input"
                  />
                </div>

                {!editingMember && (
                  <div className="space-y-2">
                    <Label htmlFor="email">E-mail *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="naam@voorbeeld.nl"
                      className="rounded-xl"
                      data-testid="member-email-input"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Rollen</Label>
                  <div className="max-h-48 space-y-1 overflow-y-auto rounded-xl border border-slate-200 p-2">
                    {ROLES.map((role) => (
                      <label key={role.value} className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-slate-50">
                        <input
                          type="checkbox"
                          checked={formData.roles.includes(role.value)}
                          onChange={() => toggleFormRole(role.value)}
                          className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                        />
                        <Shield className="w-4 h-4 text-slate-400" />
                        <span>{role.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="p-3 bg-slate-50 rounded-xl text-sm text-slate-600 max-h-56 overflow-y-auto">
                  <p className="font-medium mb-1">Rol rechten:</p>
                  <ul className="space-y-1 text-xs">
                    {ROLES.map((role) => {
                      const moduleLabels = roleCapabilityModuleLabels(capabilities, role.value);
                      return (
                        <li key={role.value}>
                          <strong>{role.label}:</strong>{' '}
                          {moduleLabels.length
                            ? `standaardrechten in ${moduleLabels.join(', ')}`
                            : 'geen extra module-/capabilityrechten via deze rol'}
                        </li>
                      );
                    })}
                  </ul>
                </div>

                {editingMember && visibleCapabilityCount > 0 && (
                  <div className="space-y-3">
                    <div>
                      <Label>Rolrechten (optioneel)</Label>
                      <p className="mt-1 text-xs text-slate-500">
                        Overschrijf alleen uitzonderingen. Rechten zijn gegroepeerd per module; moduletoegang staat waar beschikbaar bovenaan.
                      </p>
                    </div>
                    <div className="space-y-2">
                      {capabilityGroups.map((group) => (
                        <details key={group.module} className="rounded-xl border border-slate-200 bg-white">
                          <summary className="cursor-pointer select-none px-3 py-2.5 text-sm font-medium text-slate-800">
                            <span>{group.label}</span>
                            <span className="ml-2 text-xs font-normal text-slate-400">
                              {group.capabilities.length} {group.capabilities.length === 1 ? 'recht' : 'rechten'}
                            </span>
                          </summary>
                          <div className="space-y-2 border-t border-slate-100 p-3">
                            {group.capabilities.map((capability) => {
                              const overrideValue = permissionOverrides[capability.key];
                              const selectValue = overrideValue === true ? 'allow' : overrideValue === false ? 'deny' : 'default';
                              return (
                                <div key={capability.key} className="flex flex-col gap-2 rounded-lg px-1 py-1 sm:flex-row sm:items-center sm:justify-between">
                                  <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <span className="text-sm text-slate-700">{capability.label}</span>
                                      {capability.is_module_access && (
                                        <Badge variant="outline" className="text-[10px]">Moduletoegang</Badge>
                                      )}
                                    </div>
                                    {capability.canonical_permission && capability.canonical_permission !== capability.key && (
                                      <p className="mt-0.5 truncate text-[11px] text-slate-400">{capability.key}</p>
                                    )}
                                  </div>
                                  <Select
                                    value={selectValue}
                                    onValueChange={(value) => setOverride(capability.key, value === 'default' ? null : value === 'allow')}
                                  >
                                    <SelectTrigger className="w-full rounded-xl sm:w-44 sm:shrink-0">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="default">Standaard (via rol)</SelectItem>
                                      <SelectItem value="allow">Altijd toestaan</SelectItem>
                                      <SelectItem value="deny">Altijd weigeren</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              );
                            })}
                          </div>
                        </details>
                      ))}
                    </div>
                  </div>
                )}

                {editingMember && canGrantWorshipAccess && (
                  <div className="space-y-2 rounded-xl border border-teal-100 bg-teal-50 p-3">
                    <Label>Toegang binnen Worship</Label>
                    <p className="text-xs text-slate-600">
                      Wijs hier persoonlijke uitzonderingen toe. Rollen geven daarnaast standaardtoegang.
                    </p>
                    <WorshipPermissionControls
                      member={editingMember}
                      savingPermission={worshipPermissionSaving}
                      onChange={updateWorshipPermission}
                    />
                  </div>
                )}

                <DialogFooter className="gap-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setDialogOpen(false)}
                    disabled={Boolean(worshipPermissionSaving)}
                    className="rounded-xl"
                  >
                    Annuleren
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={worshipPermissionSaving}
                    className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
                    data-testid="save-member-btn"
                  >
                    {worshipPermissionSaving ? 'Worshiprechten opslaan…' : editingMember ? 'Opslaan' : 'Uitnodigen'}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default TeamPage;
